import { COURSE_TRANSLATION_LANGUAGES } from '@duke-hq/shared-constants/courseTranslationLanguages';
import { trpcAdmin, type RouterOutputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { TranslatableResourceKind } from './useContentTranslations';

type CacheData = RouterOutputs['contentTranslations']['listByResourceVersion'];
type CacheRow = CacheData['rows'][number];

// A PENDING row with no artefact yet, indistinguishable from a real
// freshly-enqueued row to `deriveTranslationStatus` (both derive the blue
// breathing state), so the optimistic -> real swap shows no flicker.
function buildSyntheticRow(languageCode: string, sourceId: string, now: Date): CacheRow {
  return {
    language: languageCode,
    status: 'PENDING',
    fileDrn: null,
    sourceId: sourceId,
    // Reads "Queued" until the worker stamps startedAt.
    startedAt: null,
    generatedAt: null,
    updatedAt: now,
    // No coverage or generation details yet; the worker sets these on completion.
    untranslatedSegmentCount: null,
    totalSegmentCount: null,
    model: null,
    engineFingerprint: null,
  };
}

// Closes the dead beat between clicking Publish and seeing translation feedback.
// Call in the publish mutation's `onSuccess`: it writes synthetic PENDING rows
// into the translations cache, so the panel and toolbar dot start streaming in
// the instant you publish, not on the next poll.
//
// PENDING only, never AVAILABLE: a finished-artefact claim a student could be
// served must never be optimistic. The first refetch replaces these with server
// truth, so no timer or failure toast is needed; a genuinely failed language
// surfaces as a FAILED row with its own Retry.
export function useOptimisticPublishTranslations() {
  const utils = trpcAdmin.useUtils();

  return (resourceVersionId: number, resourceKind: TranslatableResourceKind) => {
    const sourceId = String(resourceVersionId);
    const now = new Date();
    const queryKey = { resourceVersionId, resourceKind };

    utils.contentTranslations.listByResourceVersion.setData(queryKey, (previous) => {
      const existingByLanguage = new Map((previous?.rows ?? []).map((row) => [row.language, row]));

      // One per target language with no genuine row yet; never clobber a real one.
      const syntheticRows = COURSE_TRANSLATION_LANGUAGES.filter(
        (language) => !existingByLanguage.has(language.code),
      ).map((language) => buildSyntheticRow(language.code, sourceId, now));

      // Seed the new version so AVAILABLE-but-older rows derive `stale`, not a
      // stale empty panel that was never opened.
      return {
        currentPublishedVersionId: sourceId,
        rows: [...(previous?.rows ?? []), ...syntheticRows],
      };
    });

    void utils.contentTranslations.listByResourceVersion.invalidate(queryKey);
  };
}
